import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'Store/index';
import { Input } from 'App/shadcn/ui/input';
import { Badge } from 'App/shadcn/ui/badge';
import DateTimePicker from 'App/shadcn/components/DateTimePicker';
import EntityPicker, { type EntityOption } from 'App/shadcn/components/EntityPicker';
import ClientResource from 'App/resources/Client';
// The typed resource, not the deprecated `Resources/Contact` — `search` (which
// scopes contacts to a company and its related companies) only lives here.
import ContactResource from 'App/resources/Contact';
import UserResource from 'App/resources/User';
import ProjectResource from 'App/resources/Project';
import AppService from 'App/legacy/AppService';

export type EntityKind =
	| 'client'
	| 'contact'
	| 'user'
	| 'project'
	| 'activityType'
	| 'appointmentType'
	| 'stage'
	| 'product'
	| 'category'
	| 'clientCategory'
	| 'contactCategory'
	| 'activityCategory';

export const ENTITY_FIELDS: Record<string, EntityKind> = {
	client: 'client',
	account: 'client',
	contact: 'contact',
	contacts: 'contact',
	user: 'user',
	users: 'user',
	assigneduser: 'user',
	regby: 'user',
	project: 'project',
	projects: 'project',
	activitytype: 'activityType',
	appointmenttype: 'appointmentType',
	stage: 'stage',
	product: 'product',
	products: 'product',
	category: 'category',
	categories: 'category'
};

export const MULTI_FIELDS = new Set(['contacts', 'users', 'projects', 'products', 'categories']);

/** Entity aliases whose records are meetings. Their type field is named
 *  `activityType` like an activity's, but takes its options from the
 *  appointment side of the activity_type table — see `entityKindFor`. */
const APPOINTMENT_ENTITIES = new Set(['appointment', 'meeting']);

/** Categories are per-entity lists that happen to share the field name
 *  `categories`. Which list a card should offer follows from its entity —
 *  see `entityKindFor`. */
const CATEGORY_KIND_BY_ENTITY: Record<string, EntityKind> = {
	client: 'clientCategory',
	account: 'clientCategory',
	company: 'clientCategory',
	contact: 'contactCategory',
	person: 'contactCategory',
	activity: 'activityCategory',
	task: 'activityCategory',
	todo: 'activityCategory',
	phonecall: 'activityCategory',
	appointment: 'activityCategory',
	meeting: 'activityCategory'
};

/**
 * The picker kind for one payload field, given the entity the proposal is for.
 *
 * Two keys are ambiguous, both because a field name is shared across entities
 * that draw from different lists:
 *
 * - An appointment's type field is ALSO called `activityType` — `crmEntityMap`
 *   tells the agent to send it that way, and the v2 mapping parses
 *   `activityType` into `activityTypeId` for both entities. Without the entity
 *   we resolved it against the activity types every time, so a meeting card
 *   offered the wrong list (the appointment branch was unreachable: nothing
 *   ever emits a key literally named `appointmentType`).
 * - `categories` exists on companies, contacts and activities, each with its
 *   own list. Without the entity we offered all three concatenated, so a
 *   company card listed contact and activity categories too.
 */
export function entityKindFor(key: string, entity?: string): EntityKind | undefined {
	const kind = ENTITY_FIELDS[key.toLowerCase()];
	const alias = entity?.toLowerCase();
	if (kind === 'activityType' && alias && APPOINTMENT_ENTITIES.has(alias)) {
		return 'appointmentType';
	}
	if (kind === 'category' && alias) {
		// Unknown entity keeps the old all-three behaviour rather than showing
		// an empty picker — a wrong-but-populated list beats no list at all.
		return CATEGORY_KIND_BY_ENTITY[alias] ?? 'category';
	}
	return kind;
}

/** Sync-resolved ref fields read their option list from AppService —
 *  no network call required. Returns null for fields backed by a remote
 *  search (client / contact / project).
 *
 *  `rights` narrows activity / appointment types to the ones the user's roles
 *  allow, which is what EditAppointment does (`getInitialState.ts`). Pass it
 *  when offering choices; leave it off when resolving the display name of a
 *  value already on the record, so a role-gated type still reads as its name
 *  rather than "#5". */
function getSyncOptions(kind: EntityKind, rights = false): EntityOption[] | null {
	switch (kind) {
		case 'activityType':
			return AppService.getActivityTypes('activity', rights) as EntityOption[];
		// Appointment types are activity types scoped to 'appointment'. This used to call a
		// getAppointmentTypes() that exists on no AppService, Angular or standalone, so the
		// optional call always yielded [] and appointment-type fields never had options.
		case 'appointmentType':
			return AppService.getActivityTypes('appointment', rights) as EntityOption[];
		case 'stage': {
			const all: EntityOption[] = [];
			for (const t of ['opportunity', 'order', 'lost'] as const) {
				all.push(...(AppService.getStages(t) as unknown as EntityOption[]));
			}
			return all;
		}
		case 'product':
			return AppService.getProducts() as unknown as EntityOption[];
		case 'clientCategory':
			return AppService.getCategories('account') as unknown as EntityOption[];
		case 'contactCategory':
			return AppService.getCategories('contact') as unknown as EntityOption[];
		case 'activityCategory':
			return AppService.getCategories('activity') as unknown as EntityOption[];
		// Fallback for a card whose entity we don't recognise — every list at
		// once, which is what every card used to get.
		case 'category': {
			const all: EntityOption[] = [];
			for (const t of ['account', 'contact', 'activity']) {
				all.push(...(AppService.getCategories(t) as unknown as EntityOption[]));
			}
			return all;
		}
		default:
			return null;
	}
}

export function isDateString(value: unknown): boolean {
	if (typeof value !== 'string') return false;
	return /^\d{4}-\d{2}-\d{2}/.test(value);
}

export function formatFieldName(key: string): string {
	return key
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]/g, ' ')
		.replace(/^./, s => s.toUpperCase())
		.trim();
}

export function isNestedObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function formatNestedValue(value: Record<string, unknown>): string {
	if ('name' in value && value.name) return String(value.name);
	if ('id' in value) return `#${value.id}`;
	return JSON.stringify(value);
}

/** True for an array whose items are plain objects (line items like orderRow),
 *  excluding entity-ref fields (users / contacts) which render as pickers. */
function isObjectArray(key: string, value: unknown): value is Record<string, unknown>[] {
	if (ENTITY_FIELDS[key.toLowerCase()]) return false;
	return Array.isArray(value) && value.length > 0 && value.every(isNestedObject);
}

/** Drop a flat `<ref>Id` key when the nested `<ref>` object is also present —
 *  the object already shows the name (same as how client / contact / user are
 *  shown). e.g. an order row with {product:{id,name}, productId} hides productId. */
function visibleObjectEntries(obj: Record<string, unknown>): [string, unknown][] {
	return Object.entries(obj).filter(([k]) => {
		if (/Id$/.test(k)) {
			const base = k.slice(0, -2);
			if (isNestedObject(obj[base])) return false;
		}
		return true;
	});
}

/** Render a single value to a display string: nested ref → its name, number →
 *  localized, array → comma-joined (refs by name). */
function formatScalarValue(value: unknown): string {
	if (value === null || value === undefined || value === '') return '—';
	if (isNestedObject(value)) return formatNestedValue(value);
	if (Array.isArray(value)) {
		return value.map(v => (isNestedObject(v) ? formatNestedValue(v) : String(v))).join(', ');
	}
	if (typeof value === 'number') return value.toLocaleString();
	return String(value);
}

function extractId(obj: unknown): number | string | null {
	if (!isNestedObject(obj)) return null;
	const id = obj.id ?? obj.userId ?? obj.contactId ?? obj.clientId;
	return id === undefined || id === null ? null : (id as number | string);
}

function extractName(obj: unknown): string {
	if (!isNestedObject(obj)) return '';
	if (typeof obj.name === 'string' && obj.name) return obj.name;
	const fn = typeof obj.firstName === 'string' ? obj.firstName : '';
	const ln = typeof obj.lastName === 'string' ? obj.lastName : '';
	const full = `${fn} ${ln}`.trim();
	if (full) return full;
	if (typeof obj.email === 'string') return obj.email;
	return '';
}

export function toOption(obj: unknown): EntityOption | null {
	const id = extractId(obj);
	if (id === null) return null;
	return { id, name: extractName(obj) };
}

type AnyResource = {
	find: (params: Record<string, unknown>) => Promise<{ data: Array<Record<string, unknown>> }>;
	get?: (id: number | string) => Promise<{ data: Record<string, unknown> }>;
};

function getResource(kind: EntityKind): AnyResource | null {
	if (kind === 'client') return ClientResource as unknown as AnyResource;
	if (kind === 'contact') return ContactResource as unknown as AnyResource;
	if (kind === 'user') return UserResource as unknown as AnyResource;
	if (kind === 'project') return ProjectResource as unknown as AnyResource;
	return null;
}

const SEARCH_LIMIT = 20;

async function searchCrmEntity(kind: EntityKind, term: string): Promise<EntityOption[]> {
	const resource = getResource(kind);
	if (!resource) return [];
	try {
		const trimmed = term.trim();
		const filters: Record<string, unknown> = { limit: SEARCH_LIMIT, sort: [{ a: 'name', s: 'A' }] };
		if (trimmed) filters.q = [{ a: 'name', c: 'src', v: trimmed }];
		const res = await resource.find(filters);
		const rows = Array.isArray(res?.data) ? res.data : [];
		return rows.map(toOption).filter((v): v is EntityOption => Boolean(v));
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[FieldGrid] searchCrmEntity failed', { kind, term, err });
		return [];
	}
}

/** An id off a ref field, accepting every shape the payload uses: a nested
 *  `{id}` (single-entity cards) or a bare number / numeric string (the
 *  multi-action card sends primitives). */
function refId(raw: unknown): number | null {
	const id = typeof raw === 'number' || typeof raw === 'string' ? raw : extractId(raw);
	if (id === null || id === undefined || id === '') return null;
	const n = Number(id);
	return Number.isFinite(n) ? n : null;
}

/** The company the card is about, if it has one. Read off whichever key holds
 *  the client ref (`client` on most payloads, `account` on some). */
function clientIdFrom(data: Record<string, unknown>): number | null {
	for (const [key, value] of Object.entries(data)) {
		if (ENTITY_FIELDS[key.toLowerCase()] !== 'client') continue;
		const id = refId(Array.isArray(value) ? value[0] : value);
		if (id !== null) return id;
	}
	return null;
}

/**
 * Contacts, scoped to the card's company.
 *
 * `ContactResource.search` is the same helper the real modals use: it searches
 * name / email / phone / company name and scopes to `client.id` OR
 * `connectedClients.relatedToClientId`, so contacts at related companies and
 * sub-accounts still show. We used to run a bare `find` with no client filter
 * at all, which listed the first 20 contacts in the entire CRM — on a meeting
 * for one company, the picker offered contacts from every other one.
 *
 * Without a client there is nothing to scope by (e.g. a to-do), so fall back
 * to the unscoped search rather than showing an empty list.
 */
async function searchContacts(term: string, clientId: number | null): Promise<EntityOption[]> {
	if (clientId === null) return searchCrmEntity('contact', term);
	try {
		const res = await ContactResource.search(term.trim(), clientId, SEARCH_LIMIT);
		const rows = Array.isArray(res?.data) ? res.data : [];
		return rows.map(toOption).filter((v): v is EntityOption => Boolean(v));
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[FieldGrid] searchContacts failed', { term, clientId, err });
		return [];
	}
}

/** The appointment type a new meeting falls back to. Mirrors `Appointment.new()`
 *  (ui/app/upsales/common/resources/appointment.js): the customer's configured
 *  default, then the "first meeting" type, then whatever heads the list. */
function defaultAppointmentType(): EntityOption | null {
	const types = (getSyncOptions('appointmentType', true) ?? []) as Array<EntityOption & { first?: boolean }>;
	if (!types.length) return null;
	const defaultId = AppService.getMetadata()?.params?.DefaultAppointmentTypeId;
	const chosen = types.find(t => t.id === defaultId) ?? types.find(t => t.first) ?? types[0];
	return { id: chosen.id, name: chosen.name };
}

/**
 * Repairs a meeting proposal whose `activityType` is not an appointment type.
 *
 * Nothing on the write path checks that an appointment's type comes from the
 * appointment side of the table, so a proposal can arrive holding an activity
 * type's id — or none at all. Either way the card shows a bare "#5" and the
 * saved meeting reads as mistyped in the CRM.
 *
 * Only an unusable value is replaced: a type the agent picked that IS a valid
 * appointment type is always kept. Returns `data` unchanged when there is
 * nothing to repair, so it is safe in a state initialiser.
 */
export function withValidAppointmentType(
	entity: string | undefined,
	data: Record<string, unknown>
): Record<string, unknown> {
	if (!entity || !APPOINTMENT_ENTITIES.has(entity.toLowerCase())) return data;
	// Unfiltered: a valid type the user's roles hide is still the agent's
	// choice and must not be overwritten.
	const types = getSyncOptions('appointmentType') ?? [];
	if (!types.length) return data;
	const currentId = refId(data.activityType);
	if (currentId !== null && types.some(t => Number(t.id) === currentId)) return data;
	const fallback = defaultAppointmentType();
	return fallback ? { ...data, activityType: fallback } : data;
}

async function fetchCrmEntityById(kind: EntityKind, id: number | string): Promise<EntityOption | null> {
	const resource = getResource(kind);
	if (!resource?.get) return null;
	try {
		const res = await resource.get(id);
		return toOption(res?.data ?? null);
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[FieldGrid] fetchCrmEntityById failed', { kind, id, err });
		return null;
	}
}

type Props = {
	/** Field-name → current value. Field names are case-insensitive against
	 *  `ENTITY_FIELDS` for ref-picker detection. */
	data: Record<string, unknown>;
	onChange: (next: Record<string, unknown>) => void;
	disabled?: boolean;
	/** When set, the matching row renders as a non-editable badge (used for
	 *  the `id` field on an Update proposal). */
	readOnlyKeys?: ReadonlySet<string>;
	/** The proposal's entity (`appointment`, `todo`, `order`, …). Disambiguates
	 *  the fields whose picker depends on it — see `entityKindFor`. */
	entity?: string;
};

/**
 * The shared editor surface for WriteCard / MultiActionWriteCard. Renders a
 * dl-grid of fields, picks the right editor per field type (EntityPicker for
 * refs, DateTimePicker for dates, number/text for primitives), and hydrates
 * ref names so a `{id: 1}` shows "Anna Svensson" without a roundtrip.
 */
const FieldGrid = ({ data, onChange, disabled = false, readOnlyKeys, entity }: Props) => {
	const activeUsers = useSelector(
		(state: RootState) =>
			(
				state as unknown as {
					App?: {
						userMap?: Record<
							string,
							Array<{ id: number; name?: string; firstName?: string; lastName?: string; email?: string }>
						>;
					};
				}
			).App?.userMap?.active ?? []
	);
	const userOptions = useMemo<EntityOption[]>(
		() =>
			activeUsers.map(u => ({
				id: u.id,
				name: u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || `#${u.id}`
			})),
		[activeUsers]
	);
	const userById = useMemo(() => new Map(userOptions.map(u => [u.id, u])), [userOptions]);

	const clientId = useMemo(() => clientIdFrom(data), [data]);

	const searchEntity = useCallback(
		async (kind: EntityKind, term: string): Promise<EntityOption[]> => {
			if (kind === 'user') {
				const q = term.trim().toLowerCase();
				if (!q) return userOptions.slice(0, 50);
				return userOptions.filter(u => u.name.toLowerCase().includes(q)).slice(0, 50);
			}
			if (kind === 'contact') return searchContacts(term, clientId);
			// Offering a choice, so respect the user's role access.
			const sync = getSyncOptions(kind, true);
			if (sync) {
				const q = term.trim().toLowerCase();
				if (!q) return sync.slice(0, 50);
				return sync.filter(o => o.name?.toLowerCase().includes(q)).slice(0, 50);
			}
			return searchCrmEntity(kind, term);
		},
		[userOptions, clientId]
	);

	const fetchEntityById = useCallback(
		async (kind: EntityKind, id: number | string): Promise<EntityOption | null> => {
			if (kind === 'user') return userById.get(id) ?? { id, name: `#${id}` };
			const sync = getSyncOptions(kind);
			if (sync) return sync.find(o => o.id === id) ?? { id, name: `#${id}` };
			return fetchCrmEntityById(kind, id);
		},
		[userById]
	);

	const [displayCache, setDisplayCache] = useState<Record<string, EntityOption[]>>(() => {
		const seed: Record<string, EntityOption[]> = {};
		const hydrateOne = (kind: EntityKind, raw: unknown): EntityOption | null => {
			// Accept primitive ids (multi-action sends bare numbers) by wrapping
			// them into the option shape — the sync lookup below resolves the name.
			let opt: EntityOption | null = null;
			if (typeof raw === 'number' || typeof raw === 'string') {
				opt = { id: raw, name: '' };
			} else {
				opt = toOption(raw);
			}
			if (!opt) return null;
			if (opt.name) return opt;
			if (kind === 'user') {
				const cached = userById.get(opt.id);
				if (cached?.name) return cached;
			}
			const sync = getSyncOptions(kind);
			if (sync) {
				const found = sync.find(o => o.id === opt!.id);
				if (found?.name) return found;
			}
			return opt;
		};
		for (const [key, value] of Object.entries(data)) {
			const kind = entityKindFor(key, entity);
			if (!kind) continue;
			if (Array.isArray(value)) {
				seed[key] = value.map(v => hydrateOne(kind, v)).filter((v): v is EntityOption => Boolean(v));
			} else {
				const opt = hydrateOne(kind, value);
				if (opt) seed[key] = [opt];
			}
		}
		return seed;
	});
	const hydratedRef = useRef(false);

	useEffect(() => {
		if (hydratedRef.current && userOptions.length === 0) return;
		hydratedRef.current = true;
		(async () => {
			const updates: Array<[string, EntityOption[]]> = [];
			for (const [key, value] of Object.entries(data)) {
				const kind = entityKindFor(key, entity);
				if (!kind) continue;
				const raws = Array.isArray(value) ? value : value ? [value] : [];
				const hydrated = await Promise.all(
					raws.map(async raw => {
						const existing = toOption(raw);
						// Accept bare-id refs from the registry-shape (multi-action sends
						// primitive ids). Wrap them so the picker can hydrate.
						if (!existing && (typeof raw === 'number' || typeof raw === 'string')) {
							const fetched = await fetchEntityById(kind, raw);
							return fetched ?? { id: raw, name: `#${raw}` };
						}
						if (!existing) return null;
						if (existing.name) return existing;
						const fetched = await fetchEntityById(kind, existing.id);
						return fetched ?? existing;
					})
				);
				const cleaned = hydrated.filter((v): v is EntityOption => Boolean(v));
				if (cleaned.length) updates.push([key, cleaned]);
			}
			if (updates.length) {
				setDisplayCache(prev => {
					const next = { ...prev };
					for (const [k, v] of updates) next[k] = v;
					return next;
				});
			}
		})();
	}, [data, entity, userOptions.length, fetchEntityById]);

	const handleFieldChange = useCallback(
		(key: string, value: unknown) => {
			onChange({ ...data, [key]: value });
		},
		[data, onChange]
	);

	const handleEntityPick = useCallback(
		(key: string, next: EntityOption | EntityOption[] | null) => {
			if (Array.isArray(next)) {
				setDisplayCache(prev => ({ ...prev, [key]: next }));
				handleFieldChange(
					key,
					next.map(({ id }) => ({ id }))
				);
			} else if (next) {
				setDisplayCache(prev => ({ ...prev, [key]: [next] }));
				handleFieldChange(key, { id: next.id });
			} else {
				setDisplayCache(prev => ({ ...prev, [key]: [] }));
				handleFieldChange(key, null);
			}
		},
		[handleFieldChange]
	);

	return (
		<dl className="pr-wcard-fields">
			{Object.entries(data)
				// Object-arrays (line items like orderRow) render as a titled
				// sub-group; push them below the scalar/ref fields so summary
				// fields (description, dates, refs) read first. Stable sort keeps
				// the relative order of everything else.
				.sort(([ka, va], [kb, vb]) => (isObjectArray(ka, va) ? 1 : 0) - (isObjectArray(kb, vb) ? 1 : 0))
				.map(([key, value]) => {
					if (readOnlyKeys?.has(key)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<Badge variant="secondary" className="text-body-sm">
										{`#${String(value)}`}
									</Badge>
								</dd>
							</React.Fragment>
						);
					}

					const entityKind = entityKindFor(key, entity);
					if (entityKind) {
						const isMulti = MULTI_FIELDS.has(key.toLowerCase()) || Array.isArray(value);
						const cachedOptions = displayCache[key] ?? [];
						if (isMulti) {
							return (
								<React.Fragment key={key}>
									<dt>{formatFieldName(key)}</dt>
									<dd>
										<EntityPicker
											ghost
											multiple
											value={cachedOptions}
											onChange={next => handleEntityPick(key, next)}
											search={term => searchEntity(entityKind, term)}
											placeholder={`Search ${formatFieldName(key).toLowerCase()}…`}
											disabled={disabled}
										/>
									</dd>
								</React.Fragment>
							);
						}
						const current = cachedOptions[0] ?? null;
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<EntityPicker
										ghost
										value={current}
										onChange={next => handleEntityPick(key, next)}
										search={term => searchEntity(entityKind, term)}
										placeholder={`Search ${formatFieldName(key).toLowerCase()}…`}
										disabled={disabled}
									/>
								</dd>
							</React.Fragment>
						);
					}

					if (isNestedObject(value)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<Badge variant="outline" className="text-body-sm">
										{formatNestedValue(value)}
									</Badge>
								</dd>
							</React.Fragment>
						);
					}

					// Array of objects (line items like orderRow) — render each item as
					// an indented, titled, separated sub-grid of its own fields, reusing
					// formatFieldName so labels match the rest of the card. Generic: any
					// non-entity object-array gets this treatment.
					if (isObjectArray(key, value)) {
						return (
							<div key={key} className="pr-objarray">
								<div className="pr-objarray-title">{formatFieldName(key)}</div>
								{value.map((item, i) => (
									<div key={i} className="pr-objarray-item">
										{value.length > 1 && <div className="pr-objarray-num">{`#${i + 1}`}</div>}
										<dl className="pr-objarray-fields">
											{visibleObjectEntries(item).map(([rk, rv]) => (
												<React.Fragment key={rk}>
													<dt>{formatFieldName(rk)}</dt>
													<dd>{formatScalarValue(rv)}</dd>
												</React.Fragment>
											))}
										</dl>
									</div>
								))}
							</div>
						);
					}

					if (Array.isArray(value)) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<span className="text-body-sm" style={{ color: 'var(--pr-ink)' }}>
										{value.length ? formatScalarValue(value) : '—'}
									</span>
								</dd>
							</React.Fragment>
						);
					}

					const isDate = isDateString(value);
					const isNumber = typeof value === 'number';

					if (isDate) {
						return (
							<React.Fragment key={key}>
								<dt>{formatFieldName(key)}</dt>
								<dd>
									<DateTimePicker
										ghost
										value={String(value)}
										onChange={next => handleFieldChange(key, next)}
										disabled={disabled}
									/>
								</dd>
							</React.Fragment>
						);
					}

					return (
						<React.Fragment key={key}>
							<dt>{formatFieldName(key)}</dt>
							<dd>
								<Input
									className="h-7 text-body-sm border-transparent bg-transparent px-1.5 shadow-none hover:border-input hover:bg-background focus:border-input focus:bg-background"
									type={isNumber ? 'number' : 'text'}
									value={String(value ?? '')}
									disabled={disabled}
									onChange={e => {
										const newValue = isNumber ? Number(e.target.value) : e.target.value;
										handleFieldChange(key, newValue);
									}}
								/>
							</dd>
						</React.Fragment>
					);
				})}
		</dl>
	);
};

export default FieldGrid;
